import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { COPY, revealReaction } from "../shared/copy";
import { getLeagueState, getLeaderboardWithMovement, type Member } from "../lib/league";
import { Avatar } from "../components/Avatar";
import { rankBadge } from "../shared/format";
import { cleanProse } from "../shared/sanitize";
import type { OptionKey } from "../shared/types";

// Reveal. This is the payoff moment, so it is built to be read in order rather
// than scanned: how you did, then question by question who knew and what the
// answer actually was, then how the standings moved. Reactions stay aggregate,
// so the app never puts a name to a miss. The players do that themselves.
export function Reveal() {
  const { leagueId, dropId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);

  async function load() {
    setFailed(false);
    try {
      const { members, season } = await getLeagueState(leagueId!, user!.id);
      const { data: dqs } = await supabase.from("drop_questions").select("*").eq("drop_id", dropId).order("position");
      const dqIds = (dqs ?? []).map((d) => d.id);
      const { data: answers } = await supabase.from("answers").select("*").in("drop_question_id", dqIds);
      const byQ = new Map<string, any[]>();
      for (const a of answers ?? []) {
        const list = byQ.get(a.drop_question_id) ?? [];
        list.push(a); byQ.set(a.drop_question_id, list);
      }
      const board = season ? await getLeaderboardWithMovement(season.id, members as Member[]) : [];
      setData({ dqs: dqs ?? [], byQ, members, board });
    } catch {
      setFailed(true);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dropId, user]);

  if (failed) {
    return (
      <div className="app">
        <div className="header">
          <h1>Results</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card">
          <p className="error">We couldn&apos;t load these results.</p>
          <p className="muted">That is usually the connection. Try again.</p>
          <button className="btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="app"><div className="loading">Loading results…</div></div>;

  // Question content stays sealed until the drop actually reveals, so an empty
  // list here means the results are not out yet. Rendering the normal screen
  // showed a confident "0/0" and told the player every question was about them.
  if (data.dqs.length === 0) {
    return (
      <div className="app">
        <div className="header">
          <h1>Results</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card live">
          <span className="tag">Not yet</span>
          <p>
            These results aren&apos;t out. They post once everybody has finished or the play window closes,
            and your phone will let you know.
          </p>
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
          <button className="link" onClick={load}>Check again</button>
        </div>
      </div>
    );
  }

  // How the person reading this actually did, counting only questions they played.
  const mine = data.dqs.reduce(
    (acc: { right: number; played: number }, dq: any) => {
      const row = (data.byQ.get(dq.id) ?? []).find((r: any) => r.user_id === user!.id);
      if (!row || row.result === "subject_sitout") return acc;
      return { right: acc.right + (row.result === "correct" ? 1 : 0), played: acc.played + 1 };
    },
    { right: 0, played: 0 }
  );
  const me = data.board.find((b: any) => b.user_id === user!.id);

  return (
    <div className="app">
      <div className="header">
        <h1>Results</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>

      {/* Your score first. It is the thing everyone looks for. */}
      <div className="card live">
        <div className="score-hero">
          <div className="score">{mine.right}<span className="of">/{mine.played}</span></div>
          <p className="muted">
            {mine.played === 0
              ? "Every question this drop was about you."
              : mine.right === mine.played
              ? "Perfect. You know your people."
              : mine.right === 0
              ? "Rough one. It happens."
              : "Not bad."}
          </p>
        </div>
      </div>

      {data.dqs.map((dq: any) => {
        const rows: any[] = data.byQ.get(dq.id) ?? [];
        const eligible = rows.filter((r) => r.result !== "subject_sitout").length;
        const got = rows.filter((r) => r.result === "correct").length;
        const correct: OptionKey = dq.correct_option;
        const myRow = rows.find((r) => r.user_id === user!.id);
        const iWasSubject = dq.subject_user_id === user!.id;
        const iGotIt = myRow?.result === "correct";

        return (
          <div className="card" key={dq.id}>
            <p><b>{cleanProse(dq.question_text)}</b></p>

            <div className={`answer-reveal ${iWasSubject ? "" : iGotIt ? "hit" : "miss"}`}>
              <span className="answer-key">{correct.toUpperCase()}</span>
              <span>{dq[`option_${correct}`]}</span>
              {!iWasSubject && <span className="mark">{iGotIt ? "✅" : "❌"}</span>}
            </div>

            <p className="muted">{revealReaction(got, eligible)}</p>

            <div className="who-grid">
              {(data.members as Member[]).map((m) => {
                const a = rows.find((r) => r.user_id === m.user_id);
                const subject = m.user_id === dq.subject_user_id;
                const right = a?.result === "correct";
                return (
                  <div className={`who ${subject ? "subject" : right ? "hit" : "miss"}`} key={m.user_id}>
                    <Avatar id={m.user_id} name={m.display_name} size={34} />
                    <span className="who-name">{m.display_name.split(" ")[0]}</span>
                    <span className="who-mark">{subject ? "😏" : right ? "✅" : "❌"}</span>
                  </div>
                );
              })}
            </div>

            {dq.reveal_context && (
              <div className="context">
                <span className="tag">{COPY.reveal.labelContext}</span>
                <p>{cleanProse(dq.reveal_context)}</p>
              </div>
            )}
          </div>
        );
      })}

      {/* Where that left everyone. Movement is the point, not the list. */}
      <div className="card">
        <span className="tag">Standings</span>
        {data.board.map((e: any) => (
          <div className={`lb-row ${e.user_id === user!.id ? "me" : ""}`} key={e.user_id}>
            <span className="lb-rank">{rankBadge(e.rank, e.points)}</span>
            <Avatar id={e.user_id} name={e.name} size={30} />
            <span className="lb-name">{e.name}{e.user_id === user!.id ? " (you)" : ""}</span>
            {e.movement != null && e.movement !== 0 && (
              <span className={`move ${e.movement > 0 ? "up" : "down"}`}>
                {e.movement > 0 ? `▲${e.movement}` : `▼${Math.abs(e.movement)}`}
              </span>
            )}
            <span className="lb-points">{e.points}</span>
          </div>
        ))}
        {me?.movement != null && me.movement !== 0 && (
          <p className="muted">
            {me.movement > 0
              ? `You moved up ${me.movement} spot${me.movement === 1 ? "" : "s"}.`
              : `You slipped ${Math.abs(me.movement)} spot${Math.abs(me.movement) === 1 ? "" : "s"}.`}
          </p>
        )}
      </div>

      <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/leaderboard`)}>
        {COPY.reveal.seeStandings}
      </button>
      <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
    </div>
  );
}
